/**
 * Finalize Report Modal
 *
 * Modal for locking/finalizing an annual report and submitting to Bolagsverket.
 * Once finalized, the report cannot be edited.
 */

import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Lock,
  Send,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  FileText,
  Shield,
} from "lucide-react";
import { reportsApi, type AnnualReportSummary } from "@/lib/api";

interface FinalizeReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: AnnualReportSummary;
  companyName: string;
  onFinalized: () => void;
}

export function FinalizeReportModal({
  isOpen,
  onClose,
  report,
  companyName,
  onFinalized,
}: FinalizeReportModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmed(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const fiscalYear = `${report.fiscalYearStart.substring(0, 4)}/${report.fiscalYearEnd.substring(0, 4)}`;

  const handleFinalize = async () => {
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const response = await reportsApi.finalize(report.id);
      if (response.success) {
        toast.success("Årsredovisningen har skickats till Bolagsverket!");
        onFinalized();
        onClose();
      } else {
        const errorMessage = typeof response.error === "string"
          ? response.error
          : response.error?.message || "Kunde inte slutföra årsredovisningen";
        toast.error(errorMessage);
      }
    } catch {
      toast.error("Ett fel uppstod vid export till Bolagsverket");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      !isSubmitting &&
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Send size={20} className="text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Skicka till Bolagsverket
              </h2>
              <p className="text-sm text-gray-500">{companyName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Report info */}
          <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-4">
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <FileText size={24} className="text-gray-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Årsredovisning {fiscalYear}</p>
              <p className="text-sm text-gray-500">
                Räkenskapsår: {report.fiscalYearStart} – {report.fiscalYearEnd}
              </p>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Viktigt att veta</p>
                <ul className="text-sm text-amber-700 mt-2 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <Lock size={14} className="flex-shrink-0 mt-0.5" />
                    <span>Årsredovisningen låses permanent och kan inte längre redigeras</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Send size={14} className="flex-shrink-0 mt-0.5" />
                    <span>En iXBRL-fil genereras och skickas till Bolagsverket</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield size={14} className="flex-shrink-0 mt-0.5" />
                    <span>Du kan fortfarande ladda ner PDF-kopior</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Confirmation checkbox */}
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Jag bekräftar att årsredovisningen är korrekt och redo att skickas till Bolagsverket.
              Jag förstår att den inte kan redigeras efter detta.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="btn btn-secondary"
          >
            Avbryt
          </button>
          <button
            onClick={handleFinalize}
            disabled={!confirmed || isSubmitting}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Skickar...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Lås & skicka
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
